import { useContext } from "react";
import { queryOptions, useMutation, useQuery, useQueryClient, QueryClient } from "@tanstack/react-query";
import { Context as AuthContext } from "context/Auth";
import { parseListResponse, parseResponse } from "lib/fetch";
import { OurServiceInterface, ServiceInterface } from "types/service";
import {
    createServiceRequest,
    deleteServiceRequest,
    getOurServiceRequest,
    getOurServicesRequest,
    getServiceAvgPriceRequest,
    getServiceMinSalaryRequest,
    getServiceRequest,
    getServicesRequest,
    updateServiceRequest,
} from "lib/api/service";

const parseNumericLikeValue = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    if (value && typeof value === "object" && "payload" in value) {
        const payload = (value as { payload?: unknown }).payload;
        if (typeof payload === "string") return payload;
        if (typeof payload === "number") return String(payload);
    }
    return "";
};

const normalizeProviderCatalog = <T extends ServiceInterface>(service: T): T => {
    const providerServices = service.provider_services;
    const normalizedServices =
        providerServices && providerServices.length > 0
            ? providerServices
            : [
                  {
                      id: service.id,
                      category: service.category,
                      subcategory: service.subcategory,
                      description: service.description,
                      price: Number(service.price),
                      data: service.data,
                  },
              ];

    return {
        ...service,
        provider_services: normalizedServices,
    };
};

export const fetchServices = async (params?: HashMap<unknown>) => {
    const response = await parseListResponse<ServiceInterface>(getServicesRequest(params));
    return Object.assign(response.map(normalizeProviderCatalog), {
        active_filters: response.active_filters,
        pager: response.pager,
    });
};

export const fetchService = async (id: string) => normalizeProviderCatalog(await parseResponse<ServiceInterface>(getServiceRequest(id)));
export const fetchOurServices = async (params?: HashMap<unknown>) => {
    const response = await parseListResponse<OurServiceInterface>(getOurServicesRequest(params));
    return Object.assign(response.map(normalizeProviderCatalog), {
        active_filters: response.active_filters,
        pager: response.pager,
    });
};
export const fetchOurService = async (id: string) =>
    normalizeProviderCatalog(await parseResponse<OurServiceInterface>(getOurServiceRequest(id)));

export const createService = (data: unknown) => parseResponse<ServiceInterface>(createServiceRequest(data));

export const updateService = (id: string, data: unknown) => parseResponse<ServiceInterface>(updateServiceRequest(id, data));

export const deleteService = (id: string) => parseResponse<ServiceInterface>(deleteServiceRequest(id));

export const fetchServiceAvgPrice = async (params?: HashMap<unknown>) => {
    const raw = await parseResponse<unknown>(getServiceAvgPriceRequest(params));
    return parseNumericLikeValue(raw);
};

export const fetchServiceMinSalary = async () => {
    const raw = await parseResponse<unknown>(getServiceMinSalaryRequest());
    const numeric = Number(parseNumericLikeValue(raw));
    return Number.isFinite(numeric) ? numeric : 0;
};

export const serviceKeys = {
    all: ["service"] as const,
    lists: () => [...serviceKeys.all, "list"] as const,
    details: () => [...serviceKeys.all, "detail"] as const,
    detail: (id: string) => [...serviceKeys.details(), id] as const,
    ourLists: () => [...serviceKeys.all, "our-list"] as const,
    ourDetails: () => [...serviceKeys.all, "our-detail"] as const,
    ourDetail: (id: string) => [...serviceKeys.ourDetails(), id] as const,
};

export const serviceDetailQueryOptions = (id: string | undefined) =>
    queryOptions<ServiceInterface>({
        queryKey: id ? serviceKeys.detail(id) : serviceKeys.detail("__missing__"),
        queryFn: () => {
            if (!id) {
                return Promise.reject(new Error("Missing service id"));
            }
            return fetchService(id);
        },
        enabled: Boolean(id),
    });

export const useService = (id: string | undefined) => useQuery(serviceDetailQueryOptions(id));

export const ourServiceDetailQueryOptions = (id: string | undefined) =>
    queryOptions<OurServiceInterface>({
        queryKey: id ? serviceKeys.ourDetail(id) : serviceKeys.ourDetail("__missing__"),
        queryFn: () => {
            if (!id) {
                return Promise.reject(new Error("Missing service id"));
            }
            return fetchOurService(id);
        },
        enabled: Boolean(id),
    });

export const useOurService = (id: string | undefined) => useQuery(ourServiceDetailQueryOptions(id));

export const serviceMinSalaryQueryOptions = () =>
    queryOptions<number>({
        queryKey: [...serviceKeys.all, "min-salary"],
        queryFn: () => fetchServiceMinSalary(),
    });

export const useServiceMinSalary = () => useQuery(serviceMinSalaryQueryOptions());

export const useDeleteService = () => {
    const queryClient = useQueryClient();
    const auth = useContext(AuthContext);

    return useMutation({
        mutationFn: (id: string) => deleteService(id),
        onSuccess: async (_response, id) => {
            queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
            queryClient.invalidateQueries({ queryKey: serviceKeys.detail(id) });
            await auth.updateAccount?.();
        },
    });
};

export const useCreateService = () => {
    const queryClient = useQueryClient();
    const auth = useContext(AuthContext);

    return useMutation({
        mutationFn: (data: unknown) => createService(data),
        onSuccess: async () => {
            queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
            await auth.updateAccount?.();
        },
    });
};

export const useUpdateService = () => {
    const queryClient = useQueryClient();
    const auth = useContext(AuthContext);

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: unknown }) => updateService(id, data),
        onSuccess: async (_response, { id }) => {
            queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
            queryClient.invalidateQueries({ queryKey: serviceKeys.detail(id) });
            await auth.updateAccount?.();
        },
    });
};

export const prefetchService = (queryClient: QueryClient, id: string) =>
    queryClient.prefetchQuery(serviceDetailQueryOptions(id));
